import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { Check, Upload, FileText, ArrowRight, ArrowLeft, Loader2, Clock, CreditCard, Landmark, Wallet } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Person", "Dokumente", "Paket & Zahlung"];
const FIELDS = {
  0: [["vorname", "Vorname"], ["nachname", "Nachname"], ["geburtsdatum", "Geburtsdatum", "date"], ["telefon", "Telefon"], ["strasse", "Straße & Hausnummer"], ["plz", "PLZ"], ["ort", "Ort"]],
};
const DOCS = [["businessplan", "Businessplan"], ["lebenslauf", "Lebenslauf"], ["antrag_stellungnahme", "Antrag auf fachliche Stellungnahme"]];
const PAY_METHODS = [
  { key: "paypal", label: "PayPal", desc: "Schnell und sicher mit Ihrem PayPal-Konto.", icon: Wallet },
  { key: "card", label: "Kreditkarte, Apple Pay & Google Pay", desc: "Visa, Mastercard & Wallets über Stripe.", icon: CreditCard },
  { key: "bank", label: "Überweisung", desc: "Sie erhalten unsere Bankverbindung mit Verwendungszweck.", icon: Landmark },
];

export default function Antrag() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [app, setApp] = useState(null);
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [pkg, setPkg] = useState(params.get("pkg") || "");
  const [packages, setPackages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payMethod, setPayMethod] = useState("paypal");
  const [bankInfo, setBankInfo] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/applications").then(async (r) => {
      const draft = r.data.find((a) => a.status === "entwurf" && !a.paid && a.user_id === user.id);
      if (draft) { setApp(draft); setData(draft.form_data || {}); if (draft.lookup_key) setPkg(draft.lookup_key); }
      else { const { data } = await api.post("/applications", { form_data: {} }); setApp(data); }
    }).catch(() => {});
  }, [user]);

  const save = async (nextData = data, nextPkg = pkg) => {
    if (!app) return true;
    setSaving(true);
    try {
      await api.put(`/applications/${app.id}`, { form_data: nextData, lookup_key: nextPkg || null });
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Speichern fehlgeschlagen. Bitte erneut versuchen.");
      return false;
    } finally {
      setTimeout(() => setSaving(false), 400);
    }
  };

  const setField = (k, v) => { const nd = { ...data, [k]: v }; setData(nd); };

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind); fd.append("file", file);
    try {
      const { data: meta } = await api.post(`/applications/${app.id}/upload`, fd);
      setApp({ ...app, files: [...(app.files || []), meta] });
      toast.success(`${meta.filename} hochgeladen`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const next = async () => { const ok = await save(); if (ok) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const pay = async () => {
    if (!pkg) { toast.error("Bitte wählen Sie ein Paket."); return; }
    setPaying(true);
    const ok = await save(data, pkg);
    if (!ok) { setPaying(false); return; }
    try {
      const { data: res } = await api.post("/payments/checkout", { lookup_key: pkg, application_id: app.id, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPaying(false); }
  };

  const payPaypal = async () => {
    if (!pkg) { toast.error("Bitte wählen Sie ein Paket."); return; }
    setPaying(true);
    const ok = await save(data, pkg);
    if (!ok) { setPaying(false); return; }
    try {
      const { data: res } = await api.post("/payments/paypal-intent", { lookup_key: pkg, application_id: app.id });
      window.open(res.paypal_url, "_blank");
      toast.success("PayPal wurde geöffnet. Nach Zahlungseingang schalten wir Ihre Anfrage frei.");
      nav("/portal");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPaying(false); }
  };

  const payBank = async () => {
    if (!pkg) { toast.error("Bitte wählen Sie ein Paket."); return; }
    setPaying(true);
    const ok = await save(data, pkg);
    if (!ok) { setPaying(false); return; }
    try {
      const { data: res } = await api.post("/payments/bank-intent", { lookup_key: pkg, application_id: app.id });
      setBankInfo(res);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPaying(false); }
  };

  const handlePay = () => {
    if (payMethod === "card") return pay();
    if (payMethod === "bank") return payBank();
    return payPaypal();
  };

  if (!app) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-emerald-500" /></div>;

  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <div className={`grid place-items-center h-9 w-9 rounded-full text-sm font-semibold shrink-0 transition-colors
                ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-[#0A192F] text-white" : "bg-muted text-muted-foreground border border-border"}`}>
                {i < step ? <Check size={16} /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-2 ${i < step ? "bg-emerald-500" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        <Card className="p-6 sm:p-8 border-border shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Schritt {step + 1} / {STEPS.length}</span>
            {saving && <span className="text-xs text-emerald-600 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Gespeichert</span>}
          </div>
          <h1 className="text-2xl font-bold mb-6">{STEPS[step]}</h1>

          <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
            {step === 0 && (
              <div className="space-y-4">
                {FIELDS[0].map(([k, label, type]) => (
                  <div key={k}>
                    <Label htmlFor={k}>{label}</Label>
                    {type === "area" ? (
                      <Textarea id={k} data-testid={`field-${k}`} value={data[k] || ""} onChange={(e) => setField(k, e.target.value)} className="mt-1" rows={3} />
                    ) : (
                      <Input id={k} type={type || "text"} data-testid={`field-${k}`} value={data[k] || ""} onChange={(e) => setField(k, e.target.value)} className="mt-1" />
                    )}
                  </div>
                ))}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-2">Laden Sie Ihre Unterlagen hoch (PDF, JPG, PNG, DOCX).</p>
                {DOCS.map(([kind, label]) => {
                  const files = (app.files || []).filter((f) => f.kind === kind);
                  return (
                    <div key={kind} className="border border-border rounded-md p-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{label}</div>
                        {files.map((f) => <div key={f.id} className="text-xs text-emerald-600 flex items-center gap-1 mt-1"><FileText size={12} /> {f.filename}</div>)}
                      </div>
                      <label className="cursor-pointer">
                        <input type="file" data-testid={`upload-${kind}`} className="hidden" onChange={(e) => upload(kind, e.target.files[0])} />
                        <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-[#0A192F] text-white hover:bg-[#172A45]"><Upload size={14} /> Hochladen</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-2">Wählen Sie Ihr Paket und schließen Sie die Bestellung ab.</p>
                {packages.map((p) => (
                  <div key={p.lookup_key} data-testid={`select-pkg-${p.lookup_key}`} onClick={() => setPkg(p.lookup_key)}
                    className={`cursor-pointer border rounded-md p-4 flex items-center justify-between transition-colors
                      ${pkg === p.lookup_key ? "border-emerald-500 bg-emerald-50" : "border-border hover:border-[#0A192F]"}`}>
                    <div>
                      <div className="font-semibold flex items-center gap-2">{p.name} {p.popular && <Badge className="bg-emerald-500 text-white"><Clock size={11} className="mr-1" />48h</Badge>}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{p.desc}</div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <div className="text-xl font-extrabold font-display">{p.price} €</div>
                      <div className="text-[11px] text-muted-foreground">zzgl. 19% USt = {(p.price * 1.19).toFixed(2).replace(".", ",")} €</div>
                    </div>
                  </div>
                ))}
                <div className="pt-4">
                  <p className="text-sm font-medium mb-2">Zahlungsart</p>
                  <div className="grid gap-2">
                    {PAY_METHODS.map((m) => (
                      <div key={m.key} data-testid={`select-pay-${m.key}`} onClick={() => setPayMethod(m.key)}
                        className={`cursor-pointer border rounded-md p-3 flex items-center gap-3 transition-colors
                          ${payMethod === m.key ? "border-emerald-500 bg-emerald-50" : "border-border hover:border-[#0A192F]"}`}>
                        <m.icon size={18} className="text-[#0A192F] shrink-0" />
                        <div>
                          <div className="font-medium text-sm">{m.label}</div>
                          <div className="text-xs text-muted-foreground">{m.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          <div className="flex justify-between mt-8">
            <Button variant="outline" data-testid="btn-back" onClick={back} disabled={step === 0}><ArrowLeft size={16} className="mr-1" /> Zurück</Button>
            {step < STEPS.length - 1 ? (
              <Button data-testid="btn-next" onClick={next} className="bg-[#0A192F] hover:bg-[#172A45] text-white">Weiter <ArrowRight size={16} className="ml-1" /></Button>
            ) : (
              <Button data-testid="btn-pay" onClick={handlePay} disabled={paying || !pkg} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                {paying ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                {payMethod === "bank" ? "Bankverbindung anzeigen" : payMethod === "card" ? "Kreditkarte / Apple Pay" : "Mit PayPal bezahlen"}
              </Button>
            )}
          </div>
        </Card>
        <button onClick={() => nav("/portal")} className="mt-4 text-sm text-muted-foreground hover:text-foreground mx-auto block">Später fortsetzen (Entwurf speichern)</button>
      </div>

      <Dialog open={!!bankInfo} onOpenChange={(o) => !o && setBankInfo(null)}>
        <DialogContent data-testid="bank-details-dialog">
          <DialogHeader><DialogTitle>Überweisung – Bankverbindung</DialogTitle></DialogHeader>
          {bankInfo && (
            <div className="space-y-1 text-sm">
              <p className="text-muted-foreground mb-2">Bitte überweisen Sie den Betrag mit dem angegebenen Verwendungszweck. Nach Zahlungseingang schalten wir Ihre Anfrage frei – Ihr 48h-Countdown startet dann.</p>
              <Row label="Kontoinhaber" value={bankInfo.holder} />
              <Row label="IBAN" value={bankInfo.iban} />
              {bankInfo.bic && <Row label="BIC" value={bankInfo.bic} />}
              {bankInfo.bank && <Row label="Bank" value={bankInfo.bank} />}
              <Row label="Betrag" value={`${bankInfo.amount} € (inkl. 19% USt)`} />
              <Row label="Verwendungszweck" value={bankInfo.reference} />
            </div>
          )}
          <Button data-testid="bank-done-btn" className="mt-3 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => { setBankInfo(null); nav("/portal"); }}>Verstanden, zum Portal</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/60 py-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right break-all">{value}</span>
    </div>
  );
}
